import { useState, useEffect, useCallback } from "react";
import { getStats, triggerSync, getSyncStatus, getWebhookStatus, getWebhookUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  RefreshCcw, Users, Globe, CalendarDays, Database,
  Webhook, Copy, CheckCircle, Clock, Layers, AlertTriangle, Zap, Activity
} from "lucide-react";
import { toast } from "sonner";

function SyncLayerCard({ layerKey, layer }) {
  const isRunning = layer.status === "running";
  const isComplete = layer.status === "completed";
  const isError = layer.status === "error";

  return (
    <div
      className={`glass-card p-4 ${isRunning ? "glow-active" : ""}`}
      data-testid={`sync-layer-${layerKey}`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
          {layer.label}
        </div>
        <div className={`flex items-center gap-1.5 text-xs font-semibold ${
          isRunning ? "text-blue-400" : isComplete ? "text-emerald-400" : isError ? "text-red-400" : "text-slate-500"
        }`}>
          <div className={`w-1.5 h-1.5 rounded-full ${
            isRunning ? "bg-blue-400 animate-pulse" :
            isComplete ? "bg-emerald-400" :
            isError ? "bg-red-400" : "bg-slate-600"
          }`} />
          {isRunning ? "Calisiyor" : isComplete ? "Tamamlandi" : isError ? "Hata" : "Bekliyor"}
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <div className="text-[10px] text-slate-500">Son</div>
          <div className="text-sm font-semibold text-white mt-0.5">
            {layer.last_run
              ? new Date(layer.last_run).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })
              : "--:--"}
          </div>
        </div>
        <div>
          <div className="text-[10px] text-slate-500">Sonraki</div>
          <div className="text-sm font-semibold text-white mt-0.5">
            {layer.next_run
              ? new Date(layer.next_run).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })
              : "--:--"}
          </div>
        </div>
        <div>
          <div className="text-[10px] text-slate-500">Sync</div>
          <div className="text-sm font-semibold text-blue-400 mt-0.5">
            {layer.last_synced_count}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);
  const [webhookStatus, setWebhookStatus] = useState(null);
  const [webhookUrl, setWebhookUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [s, ss, ws] = await Promise.all([getStats(), getSyncStatus(), getWebhookStatus()]);
      setStats(s);
      setSyncStatus(ss);
      setWebhookStatus(ws);
    } catch (e) {
      console.error("Error:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    getWebhookUrl().then(setWebhookUrl).catch(() => {});
    const interval = setInterval(fetchData, 5000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleSync = async (mode) => {
    try {
      await triggerSync(mode);
      const labels = { full: "Tam senkronizasyon", "10min": "10-dakika sync", daily: "Gunluk sync", retry: "Yeniden deneme" };
      toast.success(`${labels[mode] || mode} baslatildi`);
      fetchData();
    } catch (e) {
      toast.error("Hata olustu");
    }
  };

  const fullSync = syncStatus?.full_sync;
  const multiSync = syncStatus?.multi_sync;
  const isSyncing = fullSync?.is_syncing;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-white">Dashboard</h1>
          <p className="text-sm text-slate-500 mt-0.5">Bokun rezervasyon yonetim paneli</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            data-testid="trigger-10min-sync"
            onClick={() => handleSync("10min")}
            variant="outline"
            className="rounded-lg gap-1.5 text-sm border-white/10 bg-white/[0.03] text-slate-300 hover:bg-white/[0.06] hover:text-white"
          >
            <Clock className="w-3.5 h-3.5" />
            Son 24 Saat
          </Button>
          <Button
            data-testid="trigger-full-sync"
            onClick={() => handleSync("full")}
            disabled={isSyncing}
            className="rounded-lg gap-1.5 text-sm bg-gradient-to-r from-blue-600 to-blue-500 text-white hover:from-blue-500 hover:to-blue-400 shadow-lg shadow-blue-500/20 border-0"
          >
            <RefreshCcw className={`w-3.5 h-3.5 ${isSyncing ? "sync-spinning" : ""}`} />
            {isSyncing ? "Senkronize..." : "Tam Sync"}
          </Button>
        </div>
      </div>

      <div className="page-body">
        {/* Full Sync Status */}
        {fullSync && fullSync.status !== "idle" && (
          <div
            data-testid="full-sync-status-bar"
            className={`glass-card p-4 mb-6 ${isSyncing ? "glow-active" : ""}`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-2.5 h-2.5 rounded-full ${
                  isSyncing ? "bg-blue-400 animate-pulse" :
                  fullSync.status === "completed" ? "bg-emerald-400" :
                  fullSync.status === "error" ? "bg-red-400" : "bg-slate-600"
                }`} />
                <span className="text-sm font-medium text-white">
                  {isSyncing
                    ? `Sayfa ${fullSync.current_page}/${fullSync.total_pages} - ${fullSync.total_synced} kayit`
                    : fullSync.status === "completed"
                    ? `Tamamlandi - ${fullSync.total_synced} kayit`
                    : ""}
                </span>
              </div>
              {fullSync.last_sync_time && (
                <span className="text-xs text-slate-500">
                  {new Date(fullSync.last_sync_time).toLocaleString("tr-TR")}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="stat-card" data-testid="stat-total-bookings">
            <div className="flex items-center justify-between">
              <div className="stat-label">Toplam Rezervasyon</div>
              <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <Database className="w-4 h-4 text-blue-400" />
              </div>
            </div>
            <div className="stat-value mt-1">{loading ? "..." : stats?.total_bookings ?? 0}</div>
          </div>

          <div className="stat-card" data-testid="stat-total-pax">
            <div className="flex items-center justify-between">
              <div className="stat-label">Toplam Yolcu</div>
              <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                <Users className="w-4 h-4 text-emerald-400" />
              </div>
            </div>
            <div className="stat-value mt-1">{loading ? "..." : stats?.total_pax ?? 0}</div>
          </div>

          <div className="stat-card" data-testid="stat-countries">
            <div className="flex items-center justify-between">
              <div className="stat-label">Ulkeler</div>
              <div className="w-8 h-8 rounded-lg bg-purple-500/10 flex items-center justify-center">
                <Globe className="w-4 h-4 text-purple-400" />
              </div>
            </div>
            <div className="stat-value mt-1">{loading ? "..." : stats?.unique_countries ?? 0}</div>
          </div>

          <div className="stat-card" data-testid="stat-today">
            <div className="flex items-center justify-between">
              <div className="stat-label">Bugun</div>
              <div className="w-8 h-8 rounded-lg bg-amber-500/10 flex items-center justify-center">
                <CalendarDays className="w-4 h-4 text-amber-400" />
              </div>
            </div>
            <div className="stat-value mt-1">{loading ? "..." : stats?.today_bookings ?? 0}</div>
          </div>
        </div>

        {/* Multi-Layer Sync */}
        <div className="glass-card p-6 mb-4" data-testid="multi-sync-section">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <Layers className="w-4 h-4 text-blue-400" />
              </div>
              <div>
                <div className="text-sm font-semibold text-white">Cok Katmanli Sync</div>
                <div className="text-[10px] text-slate-500 mt-0.5">Otomatik senkronizasyon sistemi</div>
              </div>
            </div>
            {multiSync?.scheduler_running && (
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/15">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-emerald-400">Aktif</span>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
            {multiSync?.layers && Object.entries(multiSync.layers).map(([key, layer]) => (
              <SyncLayerCard key={key} layerKey={key} layer={layer} />
            ))}
          </div>

          <div className="grid grid-cols-3 gap-3 pt-4 border-t border-white/[0.04]">
            <div className="text-center">
              <div className="text-xl font-bold text-white">{multiSync?.total_webhook_synced || 0}</div>
              <div className="text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-500 mt-1 flex items-center justify-center gap-1">
                <Zap className="w-3 h-3 text-amber-400" /> Webhook
              </div>
            </div>
            <div className="text-center">
              <div className="text-xl font-bold text-white">{multiSync?.retry_stats?.total_retried || 0}</div>
              <div className="text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-500 mt-1 flex items-center justify-center gap-1">
                <RefreshCcw className="w-3 h-3 text-blue-400" /> Tekrar
              </div>
            </div>
            <div className="text-center">
              <div className="text-xl font-bold text-red-400">{multiSync?.retry_stats?.total_failed || 0}</div>
              <div className="text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-500 mt-1 flex items-center justify-center gap-1">
                <AlertTriangle className="w-3 h-3 text-red-400" /> Hata
              </div>
            </div>
          </div>
        </div>

        {/* Webhook Section */}
        <div className="glass-card p-6" data-testid="webhook-section">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-8 h-8 rounded-lg bg-green-500/10 flex items-center justify-center">
              <Webhook className="w-4 h-4 text-emerald-400" />
            </div>
            <div>
              <div className="text-sm font-semibold text-white">Webhook</div>
              <div className="text-[10px] text-slate-500 mt-0.5">Gercek zamanli bildirimler</div>
            </div>
          </div>

          {webhookUrl && (
            <div className="mb-4">
              <div className="flex items-center gap-2">
                <code
                  className="flex-1 text-xs font-mono bg-black/30 border border-white/[0.06] rounded-lg px-3 py-2.5 text-blue-400 truncate"
                  data-testid="webhook-url-display"
                >
                  {webhookUrl.webhook_url}
                </code>
                <Button
                  variant="outline"
                  size="sm"
                  data-testid="copy-webhook-url-button"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(webhookUrl.webhook_url);
                      setCopied(true);
                      toast.success("Kopyalandi!");
                      setTimeout(() => setCopied(false), 2000);
                    } catch {
                      toast.error("Hata");
                    }
                  }}
                  className="rounded-lg h-9 border-white/10 bg-white/[0.03] text-slate-300 hover:bg-white/[0.06]"
                >
                  {copied ? <CheckCircle className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                </Button>
              </div>
            </div>
          )}

          {webhookStatus && (
            <div className="grid grid-cols-4 gap-3">
              {[
                { label: "Toplam", value: webhookStatus.total_received, color: "text-white" },
                { label: "Olusturma", value: webhookStatus.events?.create || 0, color: "text-emerald-400" },
                { label: "Guncelleme", value: webhookStatus.events?.update || 0, color: "text-blue-400" },
                { label: "Iptal", value: webhookStatus.events?.cancel || 0, color: "text-red-400" },
              ].map((s, i) => (
                <div key={i} className="bg-black/20 border border-white/[0.04] rounded-lg p-3 text-center">
                  <div className={`text-lg font-bold ${s.color}`}>{s.value}</div>
                  <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-600 mt-1">{s.label}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
